import { useState } from 'react'
import { Calendar as CalendarIcon, Clock } from 'lucide-react'
import { Appointment } from '../../lib/api/appointments'

interface TimeSlot {
  time: string
  hour: number
  minute: number
  appointments: Appointment[]
}

interface CalendarGridProps {
  appointments: Appointment[]
  viewMode: 'daily' | 'weekly'
  selectedDate: Date
  onTimeSlotClick: (slot: TimeSlot, date: string) => void
  onEmptySlotClick?: (date: string, time: string) => void
}

export function CalendarGrid({ 
  appointments, 
  viewMode, 
  selectedDate,
  onTimeSlotClick,
  onEmptySlotClick
}: CalendarGridProps) {
  // Generate time slots (8am to 8pm in 30-min intervals)
  const generateTimeSlots = (): string[] => {
    const slots: string[] = []
    for (let hour = 8; hour <= 20; hour++) {
      slots.push(`${hour.toString().padStart(2, '0')}:00`)
      if (hour < 20) {
        slots.push(`${hour.toString().padStart(2, '0')}:30`)
      }
    }
    return slots
  }

  const timeSlots = generateTimeSlots()

  // Get week dates starting from Monday
  const getWeekDates = (date: Date): string[] => {
    const d = new Date(date)
    const day = d.getDay()
    const diff = d.getDate() - day + (day === 0 ? -6 : 1)
    const monday = new Date(d.setDate(diff))
    
    const dates: string[] = []
    for (let i = 0; i < 7; i++) {
      const newDate = new Date(monday)
      newDate.setDate(monday.getDate() + i)
      dates.push(newDate.toISOString().split('T')[0])
    }
    return dates
  }

  const weekDates = viewMode === 'weekly' ? getWeekDates(selectedDate) : [selectedDate.toISOString().split('T')[0]]

  // Normalize time to HH:MM format
  const normalizeTime = (time: string): string => {
    const parts = time.split(':')
    const hour = parts[0].padStart(2, '0')
    const minute = parts[1]
    return `${hour}:${minute}`
  }

  // Get appointments for a specific date and time slot
  const getAppointmentsForSlot = (date: string, timeSlot: string): Appointment[] => {
    return appointments.filter(apt => {
      if (apt.date !== date) return false
      const aptStart = normalizeTime(apt.start_time)
      const aptEnd = normalizeTime(apt.end_time)
      const slot = normalizeTime(timeSlot)
      
      // Appointment starts at this slot OR overlaps this slot
      return aptStart === slot || 
             (aptStart < slot && aptEnd > slot)
    })
  }

  // Check if appointment starts at this slot
  const appointmentStartsAtSlot = (apt: Appointment, timeSlot: string): boolean => {
    return normalizeTime(apt.start_time) === normalizeTime(timeSlot)
  }

  // Calculate appointment height in slots
  const getAppointmentSlots = (apt: Appointment): number => {
    const [startHour, startMin] = apt.start_time.split(':').map(Number)
    const [endHour, endMin] = apt.end_time.split(':').map(Number)
    const startMinutes = startHour * 60 + startMin
    const endMinutes = endHour * 60 + endMin
    const duration = endMinutes - startMinutes
    return Math.ceil(duration / 30)
  }

  const formatTime = (timeStr: string) => {
    const [hours, minutes] = timeStr.split(':')
    const h = parseInt(hours)
    const ampm = h >= 12 ? 'PM' : 'AM'
    const displayHour = h % 12 || 12
    return `${displayHour}:${minutes} ${ampm}`
  }

  const getDayLabel = (date: string) => {
    const d = new Date(date)
    return d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })
  }

  const isToday = (date: string) => {
    return date === new Date().toISOString().split('T')[0]
  }

  const handleSlotClick = (timeSlot: string, date: string) => {
    const slotAppointments = getAppointmentsForSlot(date, timeSlot)
    
    // If slot is empty and onEmptySlotClick is provided, open new appointment
    if (slotAppointments.length === 0 && onEmptySlotClick) {
      onEmptySlotClick(date, timeSlot)
      return
    }
    
    // Otherwise show the detail modal
    const [hour, minute] = timeSlot.split(':').map(Number)
    const slotData: TimeSlot = {
      time: timeSlot,
      hour,
      minute,
      appointments: slotAppointments
    }
    onTimeSlotClick(slotData, date)
  }

  return (
    <div className="overflow-x-auto">
      <div className="min-w-full inline-block align-middle">
        <div className="grid" style={{ 
          gridTemplateColumns: `80px repeat(${weekDates.length}, 1fr)`,
          fontSize: viewMode === 'weekly' ? '0.75rem' : '0.875rem'
        }}>
          {/* Header Row */}
          <div className="sticky top-0 bg-base-200 border-b-2 border-base-300 p-2 font-semibold z-10">
            Time
          </div>
          {weekDates.map(date => (
            <div 
              key={date} 
              className={`sticky top-0 bg-base-200 border-b-2 border-base-300 p-2 text-center font-semibold z-10 ${
                isToday(date) ? 'text-accent' : ''
              }`}
            >
              {getDayLabel(date)}
            </div>
          ))}

          {/* Time Slots */}
          {timeSlots.map((timeSlot) => (
            <>
              <div 
                key={`time-${timeSlot}`} 
                className="border-b border-r border-base-300 p-2 text-xs text-base-content/60 bg-base-200"
              >
                {formatTime(timeSlot)}
              </div>
              {weekDates.map(date => {
                const slotAppointments = getAppointmentsForSlot(date, timeSlot)
                const startsHere = slotAppointments.filter(apt => appointmentStartsAtSlot(apt, timeSlot))
                
                return (
                  <div
                    key={`${date}-${timeSlot}`}
                    className={`border-b border-r border-base-300 p-1 min-h-[60px] relative cursor-pointer hover:bg-base-200/50 transition-colors ${
                      isToday(date) ? 'bg-accent/5' : ''
                    }`}
                    onClick={() => handleSlotClick(timeSlot, date)}
                  >
                    {startsHere.length > 0 && (
                      <div 
                        className={viewMode === 'daily' ? 'grid gap-1' : 'space-y-1'}
                        style={viewMode === 'daily' ? {
                          gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))'
                        } : undefined}
                      >
                        {viewMode === 'daily' || startsHere.length === 1 ? (
                          startsHere.map(apt => {
                            const slots = getAppointmentSlots(apt)
                            // Use status-based colors with accent as default
                            const bgColor = apt.status === 'completed' ? 'bg-success' :
                                          apt.status === 'cancelled' ? 'bg-error' :
                                          apt.status === 'confirmed' ? 'bg-info' :
                                          'bg-accent'
                            return (
                              <div
                                key={apt.id}
                                className={`${bgColor} text-white rounded p-2 text-xs overflow-hidden shadow-md`}
                                style={{ 
                                  minHeight: viewMode === 'daily' ? `${slots * 60 - 4}px` : 'auto'
                                }}
                              >
                                <div className="font-semibold truncate">{apt.customer.name}</div>
                                <div className="truncate text-[10px]">{apt.skill.name}</div>
                                <div className="truncate text-[10px] opacity-90">
                                  👤 {apt.team_member.user.full_name || apt.team_member.user.email.split('@')[0]}
                                </div>
                                <div className="text-[10px] opacity-80">
                                  {apt.start_time.substring(0, 5)} - {apt.end_time.substring(0, 5)}
                                </div>
                              </div>
                            )
                          })
                        ) : (
                          <div className="bg-accent text-white rounded p-2 text-center font-semibold shadow-md">
                            {startsHere.length} appointments
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )
              })}
            </>
          ))}
        </div>
      </div>
    </div>
  )
}
